import React, { useEffect, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useAppSelector } from "../../../store/hooks.ts";
import {
	selectReportStartDate,
	selectReportEndDate,
} from "../../../store/slices/reportsSlice.ts";
import globalAxios from "../../../globalAxios.js";
import { SERVER_URL } from "../../../config.js";

const AgeSexChart = () => {
	const chartRef = useRef<ReactECharts>(null);

	useEffect(() => {
		const handleResize = () => {
			if (chartRef.current) {
				chartRef.current.getEchartsInstance().resize();
			}
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const startDate = useAppSelector(selectReportStartDate);
	const endDate = useAppSelector(selectReportEndDate);

	const [chartData, setChartData] = useState<{
		ageGroups: string[];
		male: number[];
		female: number[];
		other: number[];
	} | null>(null);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const res = await globalAxios.post(
					`${SERVER_URL}/reports/age-sex`,
					{
						startDate,
						endDate,
					}
				);

				setChartData(res.data);
			} catch (err) {
				console.error("Failed to fetch age-sex report", err);
			}
		};

		fetchData();
	}, [startDate, endDate]);

	if (!chartData) return null;

	const { ageGroups, male, female, other } = chartData;

	// Calculate x axis (min 10 per side).
	const allValues = [...male, ...female, ...other];
	const maxValue = Math.max(...allValues.map((n) => Math.abs(n)), 10); // fallback to 10

	const totalMale = male.reduce((sum, n) => sum + n, 0);
	const totalFemale = female.reduce((sum, n) => sum + n, 0);
	const totalOther = other?.reduce((sum, n) => sum + n, 0) ?? 0;

	const options = {
		title: {
			text: "Demographics by Age and Sex",
			subtext: `Total Male: ${totalMale} | Total Female: ${totalFemale} | Total Other: ${totalOther}`,
			left: "center",
			top: 20,
			textStyle: {
				fontSize: 16,
				fontWeight: "bold",
			},
		},
		legend: {
			top: 70,
			left: "center",
			data: ["Male", "Female"],
		},
		grid: {
			top: 100,
			left: "5%",
			right: "5%",
			bottom: "5%",
			containLabel: true,
		},
		tooltip: {
			trigger: "axis",
			axisPointer: { type: "shadow" },
			formatter: (params) => {
				const ageGroup = params[0].name;
				const lines = params.map(
					(p) => `${p.marker} ${p.seriesName}: ${Math.abs(p.value)}`
				);
				return (
					`<strong>Age Group: ${ageGroup}</strong><br/>` +
					lines.join("<br/>")
				);
			},
		},
		xAxis: {
			type: "value",
			min: -maxValue,
			max: maxValue,
			axisLabel: {
				formatter: (val: number) => Math.abs(val),
			},
		},
		yAxis: {
			type: "category",
			data: ageGroups,
		},
		series: [
			{
				name: "Male",
				type: "bar",
				stack: "total",
				barCategoryGap: "0%",
				label: {
					show: true,
					position: "left",
					formatter: (val) => Math.abs(val.value),
				},
				itemStyle: { color: "#91c6e3" },
				data: male.map((n) => -n),
			},
			{
				name: "Female",
				type: "bar",
				stack: "total",
				barCategoryGap: "0%",
				label: {
					show: true,
					position: "right",
				},
				itemStyle: { color: "#f5b6c4" },
				data: female,
			},
		],
	};

	return (
		<ReactECharts
			ref={chartRef}
			option={options}
			notMerge={true}
			style={{ height: "500px", width: "100%", maxWidth: "100%" }}
		/>
	);
};

export default AgeSexChart;
